import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { WizardComponent, WizardStepComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildKvmGroupedSourceItems,
  KvmGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getKvmGroupedPartLabels,
  getMonitoringOtherProductsLabel,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { OptionDisplay, OptionDisplayService } from "@project/monitoring/option-display.service";
import { forkJoin, map, Observable, of, shareReplay, Subject, switchMap, tap } from "rxjs";
import { KvmCategorySelectionComponent } from "./kvm-category-selection/kvm-category-selection.component";
import { normalizeDeviceType, optionsMatchIgnoringFamily } from "./kvm-configuration.utils";

export enum KvmSelectorType {
  CATEGORY = "CATEGORY",
  TRANSMITTER = "TRANSMITTER",
  RECEIVER = "RECEIVER",
  ACCESSORIES = "ACCESSORIES",
  OVERVIEW = "OVERVIEW",
}

@Component({
  selector: "app-kvm-configuration",
  imports: [
    ConfigurationWizardBackButtonComponent,
    WizardComponent,
    WizardStepComponent,
    ProductConfigStepperBarComponent,
    OptionSelectorComponent,
    MonitoringOverviewComponent,
    KvmCategorySelectionComponent,
    AsyncPipe,
  ],
  templateUrl: "./kvm-configuration.component.html",
  styleUrl: "./kvm-configuration.component.scss",
})
export class KvmConfigurationComponent implements OnInit {
  protected readonly transmitterStepLabel = $localize`:@@kvmConfiguration.wizard.step1:Transmitter`;
  protected readonly extenderKitStepLabel = $localize`:@@kvmConfiguration.wizard.step1.p2p:Extender Kit`;
  protected readonly transmitterStepTitle = $localize`:@@kvmConfiguration.step2.title:Select transmitter`;
  protected readonly extenderKitStepTitle = $localize`:@@kvmConfiguration.step2.title.p2p:Select extender kit`;
  protected readonly transmitterCategoryLabel = $localize`:@@kvmConfiguration.overview.table.column2.category:Transmitter`;
  protected readonly extenderKitCategoryLabel = $localize`:@@kvmConfiguration.overview.table.column2.category.p2p:Extender Kit`;

  protected KvmSelectorTitleKeyMap: Record<KvmSelectorType, string> = {
    [KvmSelectorType.CATEGORY]: $localize`:@@kvmConfiguration.step0.title:Select category`,
    [KvmSelectorType.TRANSMITTER]: $localize`:@@kvmConfiguration.step2.title:Select transmitter`,
    [KvmSelectorType.RECEIVER]: $localize`:@@kvmConfiguration.step3.title:Select receiver`,
    [KvmSelectorType.ACCESSORIES]: $localize`:@@kvmConfiguration.step4.title:Select accessories`,
    [KvmSelectorType.OVERVIEW]: $localize`:@@kvmConfiguration.step5.title:Kvm configuration summary`,
  };

  protected readonly overviewCategoryLabelMap: Partial<Record<KvmSelectorType, string>> = {
    [KvmSelectorType.TRANSMITTER]: this.transmitterCategoryLabel,
    [KvmSelectorType.RECEIVER]: $localize`:@@kvmConfiguration.overview.table.column2.category:Receiver`,
    [KvmSelectorType.ACCESSORIES]: $localize`:@@kvmConfiguration.overview.table.column3.category:Accessories`,
  };

  protected project!: Project;
  protected originalConfiguration?: KvmConfiguration;
  protected activeStep = 0;
  protected steps = 5;
  protected needsReceiver = true;
  protected selectedCategory?: DeviceType;
  protected originalTransmitterDeviceType?: DeviceType;

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly KvmSelectorType = KvmSelectorType;
  protected groupedSections: MonitoringGroupedPartSection[] = [];
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  // Step indices for consistent family.steps array access
  private readonly STEP_INDEX = {
    TRANSMITTER: 0,
    RECEIVER: 1,
    ACCESSORIES: 2,
  } as const;

  // Consolidated wizard state flags
  private readonly wizardState = {
    initialEditHandled: false,
    originalReceiverQuantityApplied: false,
    receiverModifiedByUser: false,
  };

  private activeRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private monitoringProductService = inject(MonitoringProductService);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private productService = inject(ProductService);
  private optionDisplayService = inject(OptionDisplayService);
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected selectedCategoryForm!: FormGroup;
  protected selectedTransmitterForm!: FormGroup;
  protected selectedReceiverForm!: FormGroup;
  protected selectedAccessoriesForm!: FormGroup;

  protected family?: Family;
  protected selectedFamily$?: Observable<OptionDisplay>;
  protected selectedFamily?: OptionDisplay;
  protected selectedTransmitter?: OptionDisplay;
  protected selectedReceiver?: OptionDisplayWithQuantity;
  protected selectedAccessories?: OptionDisplayWithQuantity[];
  protected matchingReceiverDeviceIds?: string[];
  protected transmitterStep?: FamilyStep;
  protected transmitterOptionDisplays: OptionDisplay[] = [];
  protected transmitterOriginalOptions: MonitoringProductItem[] = [];
  protected receiverOriginalOptions: MonitoringProductItem[] = [];
  protected accessoriesFamily?: Family;
  protected accessoriesStep?: FamilyStep;

  protected get currentReceiverOriginalOptions(): MonitoringProductItem[] | undefined {
    // Always return the stable receiverOriginalOptions reference (or undefined when empty).
    // Returning a new array like [originalReceiver] on every call would create a new reference
    // each change-detection cycle, causing Angular to re-trigger ngOnChanges → tryRestoreOriginalOptions
    // in the child, which clears user changes.
    return this.receiverOriginalOptions.length > 0 ? this.receiverOriginalOptions : undefined;
  }

  protected get currentTransmitterStepLabel(): string {
    return this.selectedCategory === DeviceType.KVM_T_P2P ? this.extenderKitStepLabel : this.transmitterStepLabel;
  }

  protected get currentTransmitterStepTitle(): string {
    return this.selectedCategory === DeviceType.KVM_T_P2P ? this.extenderKitStepTitle : this.transmitterStepTitle;
  }

  protected get currentTransmitterCategoryLabel(): string {
    return this.selectedCategory === DeviceType.KVM_T_P2P ? this.extenderKitCategoryLabel : this.transmitterCategoryLabel;
  }

  protected get currentOverviewCategoryLabels(): KvmGroupedPartLabels {
    return {
      ...getKvmGroupedPartLabels(),
      transmitter: this.currentTransmitterCategoryLabel,
    };
  }

  constructor() {
    this.overviewItems$
      .pipe(
        switchMap((items) =>
          items.length
            ? this.monitoringGroupedPartsService.buildSections(items, MonitoringCategory.KVMS, this.otherProductsLabel)
            : of([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.kvms.find((kvm) => kvm.id === configId);
    }
    this.transmitterOriginalOptions = this.originalConfiguration?.transmitter ? [this.originalConfiguration.transmitter] : [];
    this.receiverOriginalOptions = this.originalConfiguration?.receiver ? [this.originalConfiguration.receiver] : [];

    if (this.originalConfiguration?.transmitter) {
      this.monitoringProductService
        .loadDeviceFromDB(
          this.originalConfiguration.transmitter.option.devices[0],
          MonitoringCategory.KVMS,
          this.productService.getFilterCountry(),
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((device) => {
          if (device?.deviceType === DeviceType.KVM_T_P2P) {
            this.needsReceiver = false;
            this.selectedCategory = DeviceType.KVM_T_P2P;
            this.originalTransmitterDeviceType = DeviceType.KVM_T_P2P;
          } else if (device?.deviceType === DeviceType.KVM_T_IP) {
            this.selectedCategory = DeviceType.KVM_T_IP;
            this.originalTransmitterDeviceType = DeviceType.KVM_T_IP;
          }
        });
    }
    this.getAutoSelectedFamily();
  }

  private getAutoSelectedFamily() {
    this.selectedFamily$ = this.monitoringProductService
      .loadCategoryFamiliesFromDB(MonitoringCategory.KVMS, this.productService.getFilterCountry())
      .pipe(
        switchMap((families) => this.selectFamilyForCategory(families, this.selectedCategory)),
        switchMap(({ family, optionDisplay }) => this.ensureAccessoriesStep(family).pipe(map(() => ({ family, optionDisplay })))),
        tap(({ family, optionDisplay }) => {
          this.family = family;
          this.selectedFamily = optionDisplay;
        }),
        map(({ optionDisplay }) => optionDisplay),
        shareReplay(1),
      );
  }

  private selectFamilyForCategory(
    families: Family[],
    category?: DeviceType,
  ): Observable<{ family: Family; optionDisplay: OptionDisplay }> {
    if (!families || families.length === 0) {
      throw new Error("No KVM families found in database");
    }

    const familiesWithTransmitterStep = families.filter((family) => this.hasTransmitterOptions(family));

    if (familiesWithTransmitterStep.length === 0) {
      throw new Error("No KVM family contains a transmitter step");
    }

    return forkJoin(
      familiesWithTransmitterStep.map((family) =>
        forkJoin(
          family.steps[this.STEP_INDEX.TRANSMITTER].options.map((option) =>
            this.optionDisplayService.createOptionDisplay(option, MonitoringCategory.KVMS, family),
          ),
        ).pipe(map((optionDisplays) => ({ family, optionDisplays }))),
      ),
    ).pipe(
      map((familyDisplayGroups) => {
        const allDisplays = familyDisplayGroups.flatMap((group) => group.optionDisplays);
        const matchingDisplays = category
          ? allDisplays.filter((display) => this.matchesCategory(display, category))
          : allDisplays;
        const candidateDisplays = matchingDisplays.length > 0 ? matchingDisplays : allDisplays;

        if (!candidateDisplays.length) {
          throw new Error("No KVM transmitter options found");
        }

        this.transmitterOptionDisplays = candidateDisplays;
        this.transmitterStep = new FamilyStep(
          candidateDisplays.map((display) => display.option),
          familiesWithTransmitterStep[0].steps[this.STEP_INDEX.TRANSMITTER].stepSelectionType as StepSelectionType,
        );

        const accessoriesFamily =
          families.find((family) => family.familyNumber === "KVM" && this.hasAccessoriesOptions(family)) ??
          families.find((family) => this.hasAccessoriesOptions(family));
        this.accessoriesFamily = accessoriesFamily;
        this.accessoriesStep = accessoriesFamily?.steps?.[2];

        const originalTransmitterOption = this.originalConfiguration?.transmitter?.option;
        const selectedDisplay =
          (originalTransmitterOption
            ? candidateDisplays.find((display) => optionsMatchIgnoringFamily(display.option, originalTransmitterOption))
            : undefined) ?? candidateDisplays[0];
        const selectedFamily = selectedDisplay.family ?? familiesWithTransmitterStep[0];

        return { family: selectedFamily, optionDisplay: selectedDisplay };
      }),
    );
  }

  private hasTransmitterOptions(family: Family): boolean {
    return (family.steps?.[this.STEP_INDEX.TRANSMITTER]?.options?.length ?? 0) > 0;
  }

  private hasAccessoriesOptions(family: Family): boolean {
    return (family.steps?.[this.STEP_INDEX.ACCESSORIES]?.options?.length ?? 0) > 0;
  }

  private ensureAccessoriesStep(defaultFamily: Family): Observable<void> {
    if (this.accessoriesStep) {
      return of(undefined);
    }

    return this.monitoringProductService.loadCategoryFamiliesFromDB(MonitoringCategory.KVMS).pipe(
      switchMap((allFamilies) => {
        const sharedAccessoriesFamily =
          allFamilies.find((family) => family.familyNumber === "KVM" && this.hasAccessoriesOptions(family)) ??
          allFamilies.find((family) => this.hasAccessoriesOptions(family));

        if (sharedAccessoriesFamily?.steps?.[2]) {
          this.accessoriesFamily = sharedAccessoriesFamily;
          const originalStep = sharedAccessoriesFamily.steps[2];
          this.accessoriesStep = this.explodeAccessoriesStep(originalStep);
          return of(undefined);
        }

        return this.monitoringProductService.loadAllCategoryDevicesFromDB(MonitoringCategory.KVMS).pipe(
          map((devices) =>
            devices.filter((device) => normalizeDeviceType(device.deviceType) === normalizeDeviceType(DeviceType.KVM_A)),
          ),
          map((accessoryDevices) => {
            if (!accessoryDevices.length) {
              return;
            }

            const accessoryOptions = this.createPerDeviceOptions(accessoryDevices);

            this.accessoriesFamily = defaultFamily;
            this.accessoriesStep = new FamilyStep(accessoryOptions, StepSelectionType.PICK0TON);
          }),
        );
      }),
    );
  }

  private explodeAccessoriesStep(step: FamilyStep): FamilyStep {
    const explodedOptions = step.options.flatMap((option) => {
      if (option.devices.length <= 1) {
        return [option];
      }
      return option.devices.map(
        (deviceId) =>
          new FamilyStepOption(
            [deviceId],
            option.groupName,
            option.quantityMin,
            option.quantityMax,
            option.filter1,
            option.filter2,
            option.subType,
            undefined,
            option.description,
            option.piSheetURL,
            option.configurationURL,
          ),
      );
    });
    return new FamilyStep(explodedOptions, (step.stepSelectionType as StepSelectionType) ?? StepSelectionType.PICK0TON);
  }

  private createPerDeviceOptions(
    devices: {
      matNr: string;
      name: string;
      productImageCantoID?: string;
      description?: string;
      piSheetURL?: string;
      configurationURL?: string;
    }[],
  ): FamilyStepOption[] {
    const minQuantity = 0;
    const maxQuantity = 99;

    return devices.map(
      (device) =>
        new FamilyStepOption(
          [device.matNr],
          device.name,
          minQuantity,
          maxQuantity,
          undefined,
          undefined,
          undefined,
          undefined,
          device.description,
          device.piSheetURL,
          device.configurationURL,
        ),
    );
  }

  private matchesCategory(display: OptionDisplay, category: DeviceType): boolean {
    const normalizedCategory = normalizeDeviceType(category);
    const candidateTypes =
      display.deviceTypes && display.deviceTypes.length > 0
        ? display.deviceTypes
        : display.deviceType
          ? [display.deviceType]
          : [];

    if (candidateTypes.length === 0) {
      return false;
    }

    return candidateTypes.some((type) => normalizeDeviceType(type) === normalizedCategory);
  }

  protected setActiveStep(event: number) {
    if (event === 2 && !this.needsReceiver) {
      if (this.activeStep === 1) {
        this.configurationWizard.setActiveStepValid();
        this.configurationWizard.nextStep();
      } else {
        this.configurationWizard.previousStep();
      }
    } else {
      this.activeStep = event;
    }
  }

  protected setSelectedCategory(deviceType: DeviceType) {
    if (this.isDuplicateCategoryChange(deviceType)) {
      return;
    }

    if (this.handleInitialEditCategorySync(deviceType)) {
      return;
    }

    this.performCategoryChange(deviceType);
  }

  private isDuplicateCategoryChange(deviceType: DeviceType): boolean {
    return this.selectedCategory === deviceType && !this.selectedTransmitter && this.transmitterOriginalOptions.length > 0;
  }

  private handleInitialEditCategorySync(deviceType: DeviceType): boolean {
    const isInitialEditCategorySync =
      !!this.originalConfiguration &&
      !!this.originalTransmitterDeviceType &&
      deviceType === this.originalTransmitterDeviceType &&
      !this.wizardState.initialEditHandled;

    if (!isInitialEditCategorySync) return false;

    this.selectedCategory = deviceType;
    this.needsReceiver = deviceType !== DeviceType.KVM_T_P2P;
    this.transmitterOriginalOptions = this.originalConfiguration?.transmitter ? [this.originalConfiguration.transmitter] : [];
    this.receiverOriginalOptions = this.originalConfiguration?.receiver ? [this.originalConfiguration.receiver] : [];
    this.accessoriesFamily = undefined;
    this.accessoriesStep = undefined;
    this.wizardState.originalReceiverQuantityApplied = false;
    this.wizardState.receiverModifiedByUser = false;
    this.wizardState.initialEditHandled = true;
    this.getAutoSelectedFamily();
    return true;
  }

  private performCategoryChange(deviceType: DeviceType): void {
    this.selectedCategory = deviceType;
    this.needsReceiver = deviceType !== DeviceType.KVM_T_P2P;
    this.resetTransmitterAndReceiverForms();
    this.matchingReceiverDeviceIds = undefined;
    this.transmitterStep = undefined;
    this.transmitterOptionDisplays = [];
    this.transmitterOriginalOptions = [];
    this.receiverOriginalOptions = [];
    this.accessoriesFamily = undefined;
    this.accessoriesStep = undefined;
    this.wizardState.originalReceiverQuantityApplied = false;
    this.wizardState.receiverModifiedByUser = false;
    this.getAutoSelectedFamily();
  }

  private resetTransmitterAndReceiverForms(): void {
    this.selectedTransmitterForm?.reset();
    this.selectedReceiverForm?.reset();
    this.selectedTransmitter = undefined;
    this.selectedReceiver = undefined;
  }

  protected setSelectedTransmitter($event: OptionDisplayWithQuantity[]) {
    if (!$event.length) {
      return;
    }

    const nextTransmitter = $event[0]?.optionDisplay;
    const transmitterChanged = !this.isSameTransmitter(this.selectedTransmitter, nextTransmitter);
    const shouldPreserveReceiverFromEdit =
      !!this.originalConfiguration?.receiver && !!nextTransmitter && this.isOriginalTransmitterSelection(nextTransmitter);

    this.selectedTransmitter = nextTransmitter;

    if (transmitterChanged) {
      this.wizardState.receiverModifiedByUser = false;
    }

    if (!this.needsReceiver) {
      this.selectedReceiver = undefined;
      this.matchingReceiverDeviceIds = undefined;
      this.receiverOriginalOptions = [];
      return;
    }

    if (!transmitterChanged) {
      return;
    }

    if (shouldPreserveReceiverFromEdit) {
      this.resolveMatchingReceiverForOverIp();
      return;
    }

    // Transmitter change invalidates receiver selection but keeps receiverOriginalOptions
    // so tryRestoreOriginalOptions in the child can restore with the correct quantity.
    this.selectedReceiver = undefined;
    this.selectedReceiverForm?.reset();

    this.resolveMatchingReceiverForOverIp();
  }

  protected setSelectedReceiver($event: OptionDisplayWithQuantity[]) {
    if (!$event.length) {
      return;
    }

    const nextReceiver = { ...$event[0] };
    const originalReceiver = this.originalConfiguration?.receiver;

    if (
      !!originalReceiver &&
      optionsMatchIgnoringFamily(nextReceiver.optionDisplay.option, originalReceiver.option) &&
      !this.wizardState.originalReceiverQuantityApplied
    ) {
      nextReceiver.quantity = originalReceiver.quantity;
      this.wizardState.originalReceiverQuantityApplied = true;
    }

    this.selectedReceiver = nextReceiver;
  }

  private isSameTransmitter(current?: OptionDisplay, next?: OptionDisplay): boolean {
    if (!current && !next) return true;
    if (!current || !next) return false;

    const currentDevices = current.option.devices.join("|");
    const nextDevices = next.option.devices.join("|");
    const currentFamily = current.family?.familyNumber ?? "";
    const nextFamily = next.family?.familyNumber ?? "";

    return currentDevices === nextDevices && currentFamily === nextFamily;
  }

  private isOriginalTransmitterSelection(optionDisplay: OptionDisplay): boolean {
    const originalDevices = this.originalConfiguration?.transmitter?.option?.devices ?? [];
    if (!originalDevices.length) {
      return false;
    }

    return optionDisplay.option.devices.join("|") === originalDevices.join("|");
  }

  protected setSelectedAccessories($event: OptionDisplayWithQuantity[]) {
    this.selectedAccessories = $event;
  }

  protected onSubmit() {
    this.collectOverviewData();

    if (this.activeStep === 4) {
      if (this.originalConfiguration) {
        this.updateKvmConfig(this.originalConfiguration);
      } else {
        this.createNewKvmConfig();
      }
    }

    if (!this.validateCurrentStep()) {
      return;
    }

    this.advanceToNextStep();
  }

  private validateCurrentStep(): boolean {
    switch (this.activeStep) {
      case 0:
        return this.validateCategoryStep();
      case 1:
        return this.validateTransmitterStep();
      case 2:
        return this.needsReceiver ? this.validateReceiverStep() : true;
      default:
        return true;
    }
  }

  private validateCategoryStep(): boolean {
    this.selectedCategoryForm.markAllAsTouched();
    return this.selectedCategoryForm.valid;
  }

  private validateTransmitterStep(): boolean {
    this.selectedTransmitterForm.markAllAsTouched();
    if (!this.selectedTransmitterForm.valid) return false;

    this.transmitterOriginalOptions = this.selectedTransmitter
      ? [MonitoringProductItem.create(this.selectedTransmitter.option, 1)]
      : [];

    if (this.selectedTransmitter?.family) {
      this.family = this.selectedTransmitter.family;
    }

    return true;
  }

  private validateReceiverStep(): boolean {
    this.selectedReceiverForm.markAllAsTouched();
    return this.selectedReceiverForm.valid;
  }

  private advanceToNextStep(): void {
    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  protected readonly MonitoringCategory = MonitoringCategory;

  private updateKvmConfig(config: KvmConfiguration) {
    const defaultQuantity = 1;
    config.update(
      this.family!.familyName,
      this.family!.familyNumber,
      MonitoringProductItem.create(this.selectedFamily!.option, defaultQuantity),
      MonitoringProductItem.create(this.selectedTransmitter!.option, defaultQuantity),
      this.selectedReceiver
        ? MonitoringProductItem.create(this.selectedReceiver.optionDisplay.option, this.selectedReceiver.quantity)
        : undefined,
      this.selectedAccessories?.map((accessory) =>
        MonitoringProductItem.create(accessory.optionDisplay.option, accessory.quantity),
      ),
    );
    this.backButton.navigateBack();
  }

  private createNewKvmConfig() {
    const defaultQuantity = 1;
    const kvmConfig = MonitoringConfigurationFactory.createKvm(this.family!.familyName, this.project, {
      familyNumber: this.family!.familyNumber,
      family: MonitoringProductItem.create(this.selectedFamily!.option, defaultQuantity),
      transmitter: MonitoringProductItem.create(this.selectedTransmitter!.option, defaultQuantity),
      receiver: this.selectedReceiver
        ? MonitoringProductItem.create(this.selectedReceiver.optionDisplay.option, this.selectedReceiver.quantity)
        : undefined,
      accessories: this.selectedAccessories?.map((accessory) =>
        MonitoringProductItem.create(accessory.optionDisplay.option, accessory.quantity),
      ),
    });

    this.project.addKvms(kvmConfig);
    this.backButton.navigateBack();
  }

  private collectOverviewData() {
    this.overviewItems$.next(
      buildKvmGroupedSourceItems(
        {
          transmitter: this.selectedTransmitter ? { option: this.selectedTransmitter.option, quantity: 1 } : undefined,
          receiver: this.selectedReceiver
            ? { option: this.selectedReceiver.optionDisplay.option, quantity: this.selectedReceiver.quantity }
            : undefined,
          accessories: this.selectedAccessories?.map((accessory) => ({
            option: accessory.optionDisplay.option,
            quantity: accessory.quantity,
          })),
        },
        this.currentOverviewCategoryLabels,
      ),
    );
  }

  private resolveMatchingReceiverForOverIp() {
    if (!this.selectedTransmitter || this.selectedCategory !== DeviceType.KVM_T_IP) {
      this.matchingReceiverDeviceIds = undefined;
      return;
    }

    const selectedTransmitterDeviceId = this.selectedTransmitter.option.devices[0];
    if (!selectedTransmitterDeviceId) {
      this.matchingReceiverDeviceIds = [];
      this.selectedReceiverForm?.reset();
      this.selectedReceiver = undefined;
      return;
    }

    this.monitoringProductService
      .loadDeviceFromDB(selectedTransmitterDeviceId, MonitoringCategory.KVMS, this.productService.getFilterCountry())
      .pipe(
        switchMap((transmitterDevice) => {
          const transmitterProductFamily = transmitterDevice?.productFamily?.trim();
          if (!transmitterProductFamily) {
            return of([]);
          }

          return this.monitoringProductService
            .loadAllCategoryDevicesFromDB(MonitoringCategory.KVMS, this.productService.getFilterCountry())
            .pipe(
              map((devices) =>
                devices
                  .filter(
                    (device) =>
                      normalizeDeviceType(device.deviceType) === normalizeDeviceType(DeviceType.KVM_R_IP) &&
                      device.productFamily?.trim() === transmitterProductFamily,
                  )
                  .map((device) => device.matNr),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((matchingReceiverIds) => {
        this.matchingReceiverDeviceIds = matchingReceiverIds;

        const hasOriginalReceiverForCurrentTransmitter =
          !!this.originalConfiguration?.receiver &&
          !!this.selectedTransmitter &&
          this.isOriginalTransmitterSelection(this.selectedTransmitter);

        if (hasOriginalReceiverForCurrentTransmitter) {
          this.receiverOriginalOptions = [this.originalConfiguration!.receiver!];
        }

        if (matchingReceiverIds.length === 0) {
          this.selectedReceiverForm?.reset();
          this.selectedReceiver = undefined;
          return;
        }
      });
  }
}
